import { useState, useEffect, useCallback, useRef } from "react";
import { Dropdown, Modal, Offcanvas, Toast, ToastContainer } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import "../common/Util.css";
import "./bank-trans-list.css";

import { ReactComponent as SearchIc } from "../../assets/svg/search.svg";
import { ReactComponent as ImportIc } from "../../assets/svg/import_ic.svg";
import { ReactComponent as AddIc } from "../../assets/svg/add_icon.svg";
import { ReactComponent as DownArrow } from "../../assets/svg/down_arror.svg";
import { ReactComponent as EditIc } from "../../assets/svg/edit_ic.svg";
import { ReactComponent as TrashIc } from "../../assets/svg/trash.svg";
import { ReactComponent as CalenderIc } from "../../assets/svg/add_remark.svg";
import { ReactComponent as CalIc } from "../../assets/svg/CalenderIc.svg";
import { ReactComponent as LarIc } from "../../assets/svg/lar_ic.svg";
import { ReactComponent as RarIc } from "../../assets/svg/rar_ic.svg";
import { ReactComponent as CloseIc } from "../../assets/svg/close-modal.svg";
import { ReactComponent as DownloadIc } from "../../assets/svg/download_Ics.svg";
import { ReactComponent as FileAddIc } from "../../assets/svg/file_add.svg";
import { ReactComponent as ArrowDownIc } from "../../assets/svg/arrow-down.svg";
import { ReactComponent as PlusImageIc } from "../../assets/svg/plus_image.svg";
import { Sidebar } from "../common/Sidebar";
import { Navbar } from "../common/Navbar";
import LoaderModal from "../common/loader";

import {
    getBankTransactionListApi,
    submitBankTransactionApi,
    getBankTransactionByIdApi,
    updateBankTransactionApi,
    deleteBankTransactionApi,
    addBankTransactionRemarksApi,
    getBankTransactionTemplateDownloadApi,
    uploadBankTransactionExcelApi
} from "./bank-trans-api.js";

import { getBankSuggestionsApi } from "../setup/Bank/bank-api.js";
import { downloadBase64File } from "../../services/file-download-service.js";
import BankTransactionModal from "./bank-trans-create.jsx";
import DeleteModal from "../common/deleteModel";

const getBankLogo = (bankName) => {
    if (!bankName) return "";
    const name = bankName.toLowerCase();
    if (name.includes("idfc")) {
        return "https://upload.wikimedia.org/wikipedia/commons/b/b5/IDFC_First_Bank_logo.svg";
    }
    if (name.includes("sbi") || name.includes("state bank")) {
        return "https://upload.wikimedia.org/wikipedia/commons/e/e1/State_Bank_of_India_logo.svg";
    }
    if (name.includes("hdfc")) {
        return "https://upload.wikimedia.org/wikipedia/commons/2/28/HDFC_Bank_Logo.svg";
    }
    if (name.includes("icici")) {
        return "https://upload.wikimedia.org/wikipedia/commons/1/12/ICICI_Bank_Logo.svg";
    }
    if (name.includes("axis")) {
        return "https://upload.wikimedia.org/wikipedia/commons/c/c8/Axis_Bank_logo.svg";
    }
    if (name.includes("kotak")) {
        return "https://upload.wikimedia.org/wikipedia/commons/d/de/Kotak_Mahindra_Bank_logo.svg";
    }
    return "";
};

const BankTransactionListingPage = () => {
    const fileInputRef = useRef(null);
    const tableContainerRef = useRef(null);

    // Filter and search states
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedBankFilter, setSelectedBankFilter] = useState({ value: "All", label: "All banks" });
    const [selectedTypeFilter, setSelectedTypeFilter] = useState("All");
    const [showFilter, setShowFilter] = useState("All");
    const [minAmount, setMinAmount] = useState("");
    const [maxAmount, setMaxAmount] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sortBy, setSortBy] = useState("transaction_date");
    const [sortOrder, setSortOrder] = useState("desc");

    // Table listing data
    const [transactions, setTransactions] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    // Modal / Offcanvas triggers
    const [showRemarksModal, setShowRemarksModal] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [showViewDrawer, setShowViewDrawer] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isDrawerLoading, setIsDrawerLoading] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showNewTxnModal, setShowNewTxnModal] = useState(false);

    // Bank lists for forms and dropdowns
    const [bankListOptions, setBankListOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // New/Edit transaction form states
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        bank_id: "",
        cheque_number: "",
        balance: "",
        spent_amount: "",
        transaction_date: "",
        value_date: "",
        description: "",
        remarks: ""
    });
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    // Toast snackbar notification states
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarStatus, setSnackbarStatus] = useState(true);

    // Delete confirmation states
    const [deleteId, setDeleteId] = useState(null);
    const [isOpenDelete, setIsOpenDelete] = useState(false);

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(handler);
    }, [search]);

    // Reset scroll position of the table and window/page to top when page changes
    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
        window.scrollTo({ top: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [currentPage]);

    // Fetch banks options for filter and select dropdowns
    const fetchBanks = async () => {
        try {
            const response = await getBankSuggestionsApi();
            if (response && response.data && response.data.success) {
                setBankListOptions(response.data.suggestionlist || []);
            }
        } catch (error) {
            console.error("Error loading bank list:", error);
        }
    };

    // Load initial bank list on mount
    useEffect(() => {
        fetchBanks();
    }, []);

    const prevFiltersRef = useRef({
        selectedBankFilter,
        selectedTypeFilter,
        showFilter,
        minAmount,
        maxAmount,
        startDate,
        endDate,
        debouncedSearch,
        sortBy,
        sortOrder
    });
    const fetchCounterRef = useRef(0);

    // Main fetch transaction data
    const fetchTransactions = useCallback(async () => {
        const filtersChanged =
            prevFiltersRef.current.selectedBankFilter !== selectedBankFilter ||
            prevFiltersRef.current.selectedTypeFilter !== selectedTypeFilter ||
            prevFiltersRef.current.showFilter !== showFilter ||
            prevFiltersRef.current.minAmount !== minAmount ||
            prevFiltersRef.current.maxAmount !== maxAmount ||
            prevFiltersRef.current.startDate !== startDate ||
            prevFiltersRef.current.endDate !== endDate ||
            prevFiltersRef.current.debouncedSearch !== debouncedSearch ||
            prevFiltersRef.current.sortBy !== sortBy ||
            prevFiltersRef.current.sortOrder !== sortOrder;

        if (filtersChanged) {
            prevFiltersRef.current = {
                selectedBankFilter,
                selectedTypeFilter,
                showFilter,
                minAmount,
                maxAmount,
                startDate,
                endDate,
                debouncedSearch,
                sortBy,
                sortOrder
            };
            if (currentPage !== 0) {
                setCurrentPage(0);
                return;
            }
        }

        fetchCounterRef.current += 1;
        const currentFetchId = fetchCounterRef.current;

        setIsLoading(true);
        const params = {
            limit: itemsPerPage,
            skip: currentPage * itemsPerPage,
            search: debouncedSearch
        };

        if (selectedBankFilter && selectedBankFilter.value !== "All") {
            params.bank_id = selectedBankFilter.value;
        }
        if (selectedTypeFilter && selectedTypeFilter !== "All") {
            params.created_from = selectedTypeFilter;
        }
        if (showFilter && showFilter !== "All") {
            params.show_filter = showFilter;
        }
        if (minAmount) {
            params.min_amount = minAmount;
        }
        if (maxAmount) {
            params.max_amount = maxAmount;
        }
        if (startDate) {
            params.start_date = startDate;
        }
        if (endDate) {
            params.end_date = endDate;
        }
        if (sortBy) {
            params.sort_by = sortBy;
        }
        if (sortOrder) {
            params.sort_order = sortOrder;
        }

        try {
            const response = await getBankTransactionListApi(params);
            if (currentFetchId === fetchCounterRef.current) {
                if (response && response.data && response.data.success) {
                    const txns = response.data.transactions || [];
                    const totalRec = response.data.total_rec ?? 0;

                    if (txns.length === 0 && currentPage > 0 && totalRec > 0) {
                        const lastPage = Math.ceil(totalRec / itemsPerPage) - 1;
                        setCurrentPage(lastPage >= 0 ? lastPage : 0);
                        return;
                    }

                    setTransactions(txns);
                    setTotalCount(totalRec);
                    setPageCount(response.data.total_pgs !== undefined ? response.data.total_pgs + 1 : 0);
                }
            }
        } catch (error) {
            console.error("Error loading transactions:", error);
        } finally {
            if (currentFetchId === fetchCounterRef.current) {
                setIsLoading(false);
            }
        }
    }, [itemsPerPage, currentPage, debouncedSearch, selectedBankFilter, selectedTypeFilter, showFilter, minAmount, maxAmount, startDate, endDate, sortBy, sortOrder]);

    // Refetch when dependencies change
    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
    };

    // Reset create/edit form
    const resetForm = () => {
        setFormData({
            bank_id: "",
            cheque_number: "",
            balance: "",
            spent_amount: "",
            transaction_date: "",
            value_date: "",
            description: "",
            remarks: ""
        });
        setEditingId(null);
        setErrors({});
    };

    // Open transaction modal for creation
    const handleNewTransactionClick = () => {
        resetForm();
        setShowNewTxnModal(true);
    };

    // Open transaction modal for editing
    const handleEditClick = async (id) => {
        setIsLoading(true);
        try {
            const response = await getBankTransactionByIdApi(id);
            if (response && response.data && response.data.success) {
                const txn = response.data.transaction;
                setFormData({
                    bank_id: txn.bank_id || "",
                    cheque_number: txn.cheque_number || "",
                    balance: txn.balance || "",
                    spent_amount: txn.spent_amount || "",
                    transaction_date: txn.transaction_date ? txn.transaction_date.substring(0, 10) : "",
                    value_date: txn.value_date ? txn.value_date.substring(0, 10) : "",
                    description: txn.description || "",
                    remarks: txn.remarks || ""
                });
                setEditingId(id);
                setErrors({});
                setShowNewTxnModal(true);
            } else {
                setSnackbarMessage("Failed to retrieve transaction data.");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error loading transaction:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle saving / updating transaction
    const handleSaveTransaction = async () => {
        const newErrors = {};
        if (!formData.bank_id || formData.bank_id === "Select" || formData.bank_id === "") {
            newErrors.bank_id = "Bank Name is required.";
        }
        if (!formData.balance || isNaN(formData.balance)) {
            newErrors.balance = "Balance is required and must be a valid number.";
        }
        if (!formData.spent_amount || isNaN(formData.spent_amount)) {
            newErrors.spent_amount = "Spent Amount is required and must be a valid number.";
        }
        if (!formData.transaction_date) {
            newErrors.transaction_date = "Transaction Date is required.";
        }
        if (!formData.value_date) {
            newErrors.value_date = "Value Date is required.";
        }

        if (Object.keys(newErrors).length > 0) {
            const firstMsg = Object.values(newErrors)[0];
            newErrors.general = firstMsg || "Please complete all required fields.";
            setErrors(newErrors);
            setSnackbarMessage(firstMsg || "Please complete all required fields.");
            setSnackbarStatus(false);
            setShowSnackbar(true);
            return;
        }

        setIsSaving(true);
        try {
            let response;
            if (editingId) {
                response = await updateBankTransactionApi(editingId, formData);
            } else {
                response = await submitBankTransactionApi(formData);
            }

            if (response && response.data && response.data.success) {
                setSnackbarMessage(response.data.message || (editingId ? "Transaction updated successfully!" : "Transaction saved successfully!"));
                setSnackbarStatus(true);
                setShowSnackbar(true);
                setShowNewTxnModal(false);
                resetForm();
                fetchTransactions();
            } else {
                const serverErrors = response?.data?.error || response?.data?.errors || {};
                const normalizedErrors = {};
                let actionableMessage = "";

                if (typeof serverErrors === "string" && serverErrors.trim()) {
                    actionableMessage = serverErrors.trim();
                    normalizedErrors.general = actionableMessage;
                } else if (Array.isArray(serverErrors) && serverErrors.length > 0) {
                    const firstItem = serverErrors[0];
                    actionableMessage = typeof firstItem === "string" ? firstItem : (firstItem?.message || JSON.stringify(firstItem));
                    normalizedErrors.general = actionableMessage;
                } else if (typeof serverErrors === "object" && serverErrors !== null) {
                    Object.keys(serverErrors).forEach((key) => {
                        const errVal = serverErrors[key];
                        const msg = Array.isArray(errVal) ? errVal[0] : (typeof errVal === "string" ? errVal : "");
                        if (msg) {
                            normalizedErrors[key] = msg;
                            if (!actionableMessage) actionableMessage = msg;
                        }
                    });
                }

                // If no specific field message was resolved, verify response message
                const rawMsg = response?.data?.message;
                if (!actionableMessage) {
                    if (rawMsg && !rawMsg.toLowerCase().includes("something went wrong") && rawMsg.toLowerCase() !== "validation error" && rawMsg.toLowerCase() !== "invalid data") {
                        actionableMessage = rawMsg;
                    } else if (Object.keys(normalizedErrors).length > 0) {
                        actionableMessage = Object.values(normalizedErrors)[0];
                    } else {
                        actionableMessage = "Unable to save bank transaction. Please check the entered values and ensure the selected bank is active.";
                    }
                }

                if (!normalizedErrors.general && Object.keys(normalizedErrors).length === 0) {
                    normalizedErrors.general = actionableMessage;
                }

                setErrors(normalizedErrors);
                setSnackbarMessage(actionableMessage);
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error saving transaction:", error);
            let actionableErrorMsg = "Unable to save bank transaction. Please check your network connection and try again.";
            if (error?.response?.data?.message && !error.response.data.message.toLowerCase().includes("something went wrong")) {
                actionableErrorMsg = error.response.data.message;
            } else if (error?.message && !error.message.toLowerCase().includes("something went wrong")) {
                actionableErrorMsg = error.message;
            }
            setErrors({ general: actionableErrorMsg });
            setSnackbarMessage(actionableErrorMsg);
            setSnackbarStatus(false);
            setShowSnackbar(true);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle delete click to show confirmation modal
    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setIsOpenDelete(true);
    };

    // Actual delete transaction logic
    const deleteBankTransaction = async () => {
        if (deleteId === null) return;
        setIsLoading(true);
        try {
            const response = await deleteBankTransactionApi(deleteId);
            if (response && response.data && response.data.success) {
                setSnackbarMessage(response.data.message || "Transaction deleted successfully.");
                setSnackbarStatus(true);
                setShowSnackbar(true);
                if (showViewDrawer) setShowViewDrawer(false);
                fetchTransactions();
            } else {
                setSnackbarMessage(response?.data?.message || "Failed to delete transaction.");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error deleting transaction:", error);
        } finally {
            setIsLoading(false);
            setDeleteId(null);
            setIsOpenDelete(false);
        }
    };

    // Open detailed Offcanvas view drawer
    const handleRowClick = async (row) => {
        setSelectedRow(row);
        setShowViewDrawer(true);
        setIsDrawerLoading(true);
        try {
            const response = await getBankTransactionByIdApi(row.id);
            if (response && response.data && response.data.success) {
                setSelectedRow(response.data.transaction);
            } else {
                setSnackbarMessage("Could not retrieve transaction details.");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error opening details:", error);
            setSnackbarMessage("Error retrieving transaction details.");
            setSnackbarStatus(false);
            setShowSnackbar(true);
        } finally {
            setIsDrawerLoading(false);
        }
    };

    // Open Remarks Modal
    const handleRemarksClick = (row) => {
        setSelectedRow(row);
        setRemarks(row.remarks || "");
        setShowRemarksModal(true);
    };

    // Handle saving remarks
    const handleSaveRemarks = async () => {
        if (!remarks.trim()) {
            alert("Remarks field is required.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await addBankTransactionRemarksApi(selectedRow.id, remarks);
            if (response && response.data && response.data.success) {
                setSnackbarMessage(response.data.message || "Remarks saved successfully.");
                setSnackbarStatus(true);
                setShowSnackbar(true);
                setShowRemarksModal(false);
                setRemarks("");
                // Refresh drawer detail if currently visible
                if (showViewDrawer && selectedRow?.id) {
                    const detailRes = await getBankTransactionByIdApi(selectedRow.id);
                    if (detailRes && detailRes.data && detailRes.data.success) {
                        setSelectedRow(detailRes.data.transaction);
                    }
                }
                fetchTransactions();
            } else {
                setSnackbarMessage(response?.data?.message || "Failed to update remarks.");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error saving remarks:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Download upload Excel template
    const handleDownloadTemplate = async () => {
        setIsLoading(true);
        try {
            const response = await getBankTransactionTemplateDownloadApi();
            if (response && response.data && response.data.success) {
                const base64String = response.data.base64;
                const fileName = `Bank_Transaction_Template_${Date.now()}.xlsx`;
                downloadBase64File(base64String, fileName);
                setSnackbarMessage("Template Excel downloaded successfully.");
                setSnackbarStatus(true);
                setShowSnackbar(true);
            } else {
                setSnackbarMessage(response?.data?.message || "Failed to download template.");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error downloading template:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Trigger local Excel file selector
    const handleSelectExcelFileClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle excel sheet selection via browse input
    const handleExcelFileUploaded = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
        if (event.target) {
            event.target.value = "";
        }
    };

    // Drag and drop event handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                setSelectedFile(file);
            } else {
                alert("Only Excel file format (.xlsx, .xls) is supported.");
            }
        }
    };

    // Handle excel sheet bulk upload on button click
    const handleUploadClick = async () => {
        if (!selectedFile) {
            alert("Please select a valid Excel file.");
            return;
        }

        setIsLoading(true);
        try {
            const base64String = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject("File reading failed");
                reader.readAsDataURL(selectedFile);
            });

            const response = await uploadBankTransactionExcelApi(base64String);
            if (response && response.status === 200) {
                if (response.data.success) {
                    setSnackbarMessage(response.data.message || "Excel uploaded and imported successfully!");
                    setSnackbarStatus(true);
                    setShowSnackbar(true);
                    setShowImportModal(false);
                    setSelectedFile(null);
                    fetchTransactions();
                } else {
                    setSnackbarMessage(response.data.message || "Bulk import failed. Please download the annotated error report.");
                    setSnackbarStatus(false);
                    setShowSnackbar(true);
                }

                // If Excel results sheet is returned, download it immediately
                if (response.data.base64) {
                    const fileName = `Bank_Transaction_Upload_Report_${Date.now()}.xlsx`;
                    downloadBase64File(response.data.base64, fileName);
                }
            } else {
                setSnackbarMessage(response?.data?.message || "Import upload failed.");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error uploading excel:", error);
            setSnackbarMessage("An error occurred during sheet upload.");
            setSnackbarStatus(false);
            setShowSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    // All filtering is now handled robustly on the backend to maintain accurate server-side pagination!
    const getFilteredTransactions = () => {
        return transactions;
    };

    const displayTransactions = getFilteredTransactions();

    return (
        <div className="d-flex" style={{ minHeight: "100vh" }}>
            {/* Standard Project Loader */}
            <LoaderModal show={isLoading} />

            <Sidebar currentfile="Bank Transaction" />
            <div className="flex-grow-1 d-flex flex-column">
                <Navbar pageTitle="Bank Transaction" />
                <div className="bg-half-white p-3 flex-grow-1">
                    <div className="top-card-container shadow-sm">
                        {/* Header & Actions */}
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="fs-24 fw-600">Bank Transactions - {totalCount}</div>
                            <div className="d-flex gap-3">
                                <div className="search-wrapper d-flex align-items-center gap-2 px-3 py-2">
                                    <SearchIc style={{ width: "18px", height: "18px", color: "#9CA3AF" }} />
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        className="border-0 outline-0 rounded-3 fs-14 fw-500 textcolor"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                                <button
                                    className="new-txn-btn fw-600 fs-14"
                                    onClick={handleNewTransactionClick}
                                >
                                    <AddIc style={{ width: "16px", height: "16px" }} />
                                    New Transaction
                                </button>
                                <button
                                    className="import-btn fw-600 fs-14"
                                    onClick={() => setShowImportModal(true)}
                                >
                                    <ImportIc style={{ width: "18px", height: "18px" }} />
                                    Import
                                </button>
                            </div>
                        </div>

                        {/* Filters Row */}
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: "16px", minHeight: "32px", height: "auto", opacity: 1 }}>
                            {/* Sort Filter */}
                            <Dropdown>
                                <Dropdown.Toggle variant="light" className={`sort-filter-btn ${sortBy !== "transaction_date" || sortOrder !== "desc" ? "filter-active" : ""}`}>
                                    <span className="text-muted me-1">Sort by :</span>
                                    <span className="textPurple fw-500">
                                        {sortBy === "created_at" && "Date created"}
                                        {sortBy === "transaction_date" && "Txn Date"}
                                        {sortBy === "spent_amount" && "Amount"}
                                        {sortBy === "balance" && "Balance"}
                                        {sortOrder === "asc" ? " (↑)" : " (↓)"}
                                    </span>
                                    <DownArrow className="ms-2 textPurple" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="show-filter-dropdown border-0 shadow">
                                    <Dropdown.Item active={sortBy === "created_at" && sortOrder === "desc"} onClick={() => { setSortBy("created_at"); setSortOrder("desc"); }}>Date created (Newest)</Dropdown.Item>
                                    <Dropdown.Item active={sortBy === "created_at" && sortOrder === "asc"} onClick={() => { setSortBy("created_at"); setSortOrder("asc"); }}>Date created (Oldest)</Dropdown.Item>
                                    <Dropdown.Item active={sortBy === "transaction_date" && sortOrder === "desc"} onClick={() => { setSortBy("transaction_date"); setSortOrder("desc"); }}>Txn Date (Newest)</Dropdown.Item>
                                    <Dropdown.Item active={sortBy === "transaction_date" && sortOrder === "asc"} onClick={() => { setSortBy("transaction_date"); setSortOrder("asc"); }}>Txn Date (Oldest)</Dropdown.Item>
                                    <Dropdown.Item active={sortBy === "spent_amount" && sortOrder === "desc"} onClick={() => { setSortBy("spent_amount"); setSortOrder("desc"); }}>Amount (High to Low)</Dropdown.Item>
                                    <Dropdown.Item active={sortBy === "spent_amount" && sortOrder === "asc"} onClick={() => { setSortBy("spent_amount"); setSortOrder("asc"); }}>Amount (Low to High)</Dropdown.Item>
                                    <Dropdown.Item active={sortBy === "balance" && sortOrder === "desc"} onClick={() => { setSortBy("balance"); setSortOrder("desc"); }}>Balance (High to Low)</Dropdown.Item>
                                    <Dropdown.Item active={sortBy === "balance" && sortOrder === "asc"} onClick={() => { setSortBy("balance"); setSortOrder("asc"); }}>Balance (Low to High)</Dropdown.Item>
                                    {(sortBy !== "created_at" || sortOrder !== "desc") && (
                                        <>
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                className="text-danger fw-600 text-center"
                                                onClick={() => { setSortBy("created_at"); setSortOrder("desc"); }}
                                            >
                                                Reset Sort
                                            </Dropdown.Item>
                                        </>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* Bank Master Dropdown Filter */}
                            <Dropdown>
                                <Dropdown.Toggle variant="light" className={`bank-filter-btn ${selectedBankFilter.value !== "All" ? "filter-active" : ""}`}>
                                    <span className="text-muted me-1">Bank :</span>
                                    <span className="textPurple fw-500">{selectedBankFilter.label}</span>
                                    <DownArrow className="ms-2 textPurple" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="show-filter-dropdown border-0 shadow">
                                    <Dropdown.Item
                                        active={selectedBankFilter.value === "All"}
                                        onClick={() => setSelectedBankFilter({ value: "All", label: "All banks" })}
                                    >
                                        All banks
                                    </Dropdown.Item>
                                    {bankListOptions.map((opt) => (
                                        <Dropdown.Item
                                            key={opt.value}
                                            active={selectedBankFilter.value === opt.value}
                                            onClick={() => setSelectedBankFilter({ value: opt.value, label: opt.label })}
                                        >
                                            {opt.label}
                                        </Dropdown.Item>
                                    ))}
                                    {selectedBankFilter.value !== "All" && (
                                        <>
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                className="text-danger fw-600 text-center"
                                                onClick={() => setSelectedBankFilter({ value: "All", label: "All banks" })}
                                            >
                                                Clear Filter
                                            </Dropdown.Item>
                                        </>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* Date Filter */}
                            <Dropdown>
                                <Dropdown.Toggle variant="light" className={`date-filter-btn ${startDate || endDate ? "filter-active" : ""}`}>
                                    <span className="text-muted me-1">Date Range :</span>
                                    <span className="textPurple fw-500">
                                        {startDate && endDate ? `${startDate} to ${endDate}` : (startDate ? `From ${startDate}` : (endDate ? `To ${endDate}` : "Select"))}
                                    </span>
                                    <CalIc className="ms-2 textPurple" style={{ width: "16px", height: "16px" }} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="show-filter-dropdown border-0 shadow p-2" style={{ minWidth: "300px" }}>
                                    <div className="d-flex flex-column align-items-center" onClick={(e) => e.stopPropagation()}>
                                        <DatePicker
                                            selectsRange={true}
                                            startDate={startDate ? new Date(startDate) : null}
                                            endDate={endDate ? new Date(endDate) : null}
                                            onChange={(update) => {
                                                const [start, end] = update;
                                                const formatLocalDate = (date) => {
                                                    if (!date) return "";
                                                    const yyyy = date.getFullYear();
                                                    const mm = String(date.getMonth() + 1).padStart(2, '0');
                                                    const dd = String(date.getDate()).padStart(2, '0');
                                                    return `${yyyy}-${mm}-${dd}`;
                                                };
                                                setStartDate(start ? formatLocalDate(start) : "");
                                                setEndDate(end ? formatLocalDate(end) : "");
                                            }}
                                            inline
                                            isClearable={true}
                                        />
                                        <div className="d-flex w-100 justify-content-between mt-2 px-2 pb-1 gap-2">
                                            <button
                                                className="btn btn-sm btn-outline-secondary w-50 fs-12 fw-600"
                                                onClick={() => {
                                                    setStartDate("");
                                                    setEndDate("");
                                                }}
                                            >
                                                Clear
                                            </button>
                                            <button
                                                className="btn btn-sm btn-primary w-50 fs-12 fw-600"
                                                style={{ backgroundColor: "#4F46E5", borderColor: "#4F46E5" }}
                                                onClick={() => {
                                                    document.body.click();
                                                }}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* Created From Type Filter */}
                            <Dropdown>
                                <Dropdown.Toggle variant="light" className={`type-filter-btn ${selectedTypeFilter !== "All" ? "filter-active" : ""}`}>
                                    <span className="text-muted me-1">Type :</span>
                                    <span className="textPurple fw-500">{selectedTypeFilter === "All" ? "All types" : selectedTypeFilter}</span>
                                    <DownArrow className="ms-2 textPurple" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="show-filter-dropdown border-0 shadow">
                                    <Dropdown.Item className={selectedTypeFilter === "All" ? "active" : ""} onClick={() => setSelectedTypeFilter("All")}>All types</Dropdown.Item>
                                    <Dropdown.Item className={selectedTypeFilter === "Manual" ? "active" : ""} onClick={() => setSelectedTypeFilter("Manual")}>Manual</Dropdown.Item>
                                    <Dropdown.Item className={selectedTypeFilter === "Excel" ? "active" : ""} onClick={() => setSelectedTypeFilter("Excel")}>Excel</Dropdown.Item>
                                    {selectedTypeFilter !== "All" && (
                                        <>
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                className="text-danger fw-600 text-center"
                                                onClick={() => setSelectedTypeFilter("All")}
                                            >
                                                Clear Filter
                                            </Dropdown.Item>
                                        </>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* Show Filter */}
                            <Dropdown>
                                <Dropdown.Toggle variant="light" className={`show-filter-btn ${showFilter !== "All" ? "filter-active" : ""}`}>
                                    <span className="text-muted me-1">Show :</span>
                                    <span className="textPurple fw-500">{showFilter}</span>
                                    <DownArrow className="ms-2 textPurple" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="show-filter-dropdown border-0 shadow">
                                    <Dropdown.Item className={showFilter === "All" ? "active" : ""} onClick={() => setShowFilter("All")}>All</Dropdown.Item>
                                    <Dropdown.Item className={showFilter === "Today" ? "active" : ""} onClick={() => setShowFilter("Today")}>Today</Dropdown.Item>
                                    <Dropdown.Item className={showFilter === "Yesterday" ? "active" : ""} onClick={() => setShowFilter("Yesterday")}>Yesterday</Dropdown.Item>
                                                                        <Dropdown.Item className={showFilter === "Last Week" ? "active" : ""} onClick={() => setShowFilter("Last Week")}>Last Week</Dropdown.Item>

                                    <Dropdown.Item className={showFilter === "Last Month" ? "active" : ""} onClick={() => setShowFilter("Last Month")}>Last Month</Dropdown.Item>
                                    <Dropdown.Item className={showFilter === "Last 3 Months" ? "active" : ""} onClick={() => setShowFilter("Last 3 Months")}>Last 3 Months</Dropdown.Item>
                                    {showFilter !== "All" && (
                                        <>
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                className="text-danger fw-600 text-center"
                                                onClick={() => setShowFilter("All")}
                                            >
                                                Clear Filter
                                            </Dropdown.Item>
                                        </>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>

                            {/* Amount range input filters */}
                            <div className={`range-filter-container ${minAmount !== "" || maxAmount !== "" ? "filter-active" : ""}`} style={{ width: (minAmount !== "" || maxAmount !== "") ? "220px" : "200px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <span className="text-muted">₹ Range :</span>
                                <input
                                    type="text"
                                    placeholder="Min"
                                    className="border-0 bg-transparent outline-0 text-center"
                                    style={{ width: "40px", fontSize: "12px" }}
                                    value={minAmount}
                                    onChange={(e) => setMinAmount(e.target.value)}
                                />
                                <span className="text-muted">-</span>
                                <input
                                    type="text"
                                    placeholder="Max"
                                    className="border-0 bg-transparent outline-0 text-center"
                                    style={{ width: "40px", fontSize: "12px" }}
                                    value={maxAmount}
                                    onChange={(e) => setMaxAmount(e.target.value)}
                                />
                                {(minAmount !== "" || maxAmount !== "") && (
                                    <span
                                        className="text-danger fw-bold ms-1 cursor-pointer"
                                        style={{ fontSize: "14px", lineHeight: "1" }}
                                        onClick={() => {
                                            setMinAmount("");
                                            setMaxAmount("");
                                        }}
                                    >
                                        ×
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Listing Table Container */}
                    <div className="bg-white rounded-4 mt-3 shadow-sm overflow-hidden">
                        <div className="table-responsive" ref={tableContainerRef}>
                            <table className="table table-hover mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="fs-13 fw-700 text-muted border-0" style={{ whiteSpace: "nowrap" }}>Txn Date</th>
                                        <th className="fs-13 fw-700 text-muted border-0" style={{ whiteSpace: "nowrap" }}>Value Date</th>
                                        <th className="fs-13 fw-700 text-muted border-0" style={{ whiteSpace: "nowrap" }}>Bank</th>
                                        <th className="fs-13 fw-700 text-muted border-0" style={{ minWidth: "250px", maxWidth: "350px" }}>Description</th>
                                        <th className="fs-13 fw-700 text-muted border-0">Cheque No</th>
                                        <th className="fs-13 fw-700 text-muted border-0 text-end">Amount</th>
                                        <th className="fs-13 fw-700 text-muted border-0 text-end">Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!Array.isArray(displayTransactions) || displayTransactions.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4 fs-14 text-muted">
                                                No bank transactions found.
                                            </td>
                                        </tr>
                                    ) : (
                                        displayTransactions.map((row, index) => (
                                            <tr
                                                key={row.id}
                                                className="table-row-custom"
                                                onMouseEnter={() => setHoveredIndex(index)}
                                                onMouseLeave={() => setHoveredIndex(null)}
                                                onClick={() => handleRowClick(row)}
                                            >
                                                <td className="fs-13 fw-500" style={{ whiteSpace: "nowrap" }}>
                                                     <div className="d-flex align-items-center gap-2">
                                                         {row.transaction_date}
                                                         {row.created_from === "Manual" && (
                                                             <span className="badge-m">M</span>
                                                         )}
                                                     </div>
                                                </td>
                                                <td className="fs-13 fw-500" style={{ whiteSpace: "nowrap" }}>{row.value_date}</td>
                                                <td className="fs-13 fw-500 text-muted" style={{ whiteSpace: "nowrap" }}>{row.bank_name}</td>
                                                <td className="fs-13 fw-500 text-muted" style={{ minWidth: "250px", maxWidth: "350px" }}>
                                                     <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{row.description}</div>
                                                </td>
                                                <td className="fs-13 fw-500 text-muted">{row.cheque_number || "---"}</td>
                                                <td className="fs-13 text-end">₹{Number(row.spent_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                <td className="fs-13 text-end position-relative">
                                                     <span>₹{Number(row.balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                     {hoveredIndex === index && (
                                                         <div className="action-buttons d-flex gap-2">
                                                             <div className="action-icon" onClick={(e) => { e.stopPropagation(); handleRemarksClick(row); }}><CalenderIc style={{ width: "16px" }} /></div>
                                                             <div className="action-icon" onClick={(e) => { e.stopPropagation(); handleEditClick(row.id); }}><EditIc style={{ width: "14px" }} /></div>
                                                             <div className="action-icon text-danger" onClick={(e) => { e.stopPropagation(); handleDeleteClick(row.id); }}><TrashIc style={{ width: "16px" }} /></div>
                                                         </div>
                                                     )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination Controls */}
                    <div className="d-flex align-items-center justify-content-between mt-4 px-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="fs-13 fw-500">Records per page :</span>
                            <Dropdown>
                                <Dropdown.Toggle variant="light" className="px-3 py-1 rounded-3 border bg-white fs-13 fw-600">
                                    {itemsPerPage}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => { setItemsPerPage(10); setCurrentPage(0); }}>10</Dropdown.Item>
                                    <Dropdown.Item onClick={() => { setItemsPerPage(20); setCurrentPage(0); }}>20</Dropdown.Item>
                                    <Dropdown.Item onClick={() => { setItemsPerPage(50); setCurrentPage(0); }}>50</Dropdown.Item>
                                    <Dropdown.Item onClick={() => { setItemsPerPage(100); setCurrentPage(0); }}>100</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        <ReactPaginate
                            breakLabel="..."
                            nextLabel={<RarIc />}
                            onPageChange={handlePageClick}
                            pageRangeDisplayed={3}
                            pageCount={pageCount}
                            previousLabel={<LarIc />}
                            renderOnZeroPageCount={null}
                            containerClassName="pagination d-flex gap-2 align-items-center list-unstyled mb-0"
                            pageClassName="page-item"
                            pageLinkClassName="page-link rounded-circle border-0 d-flex align-items-center justify-content-center"
                            previousClassName="page-item"
                            previousLinkClassName="page-link rounded-circle border-0 d-flex align-items-center justify-content-center"
                            nextClassName="page-item"
                            nextLinkClassName="page-link rounded-circle border-0 d-flex align-items-center justify-content-center"
                            activeClassName="active"
                            forcePage={currentPage}
                        />
                    </div>
                </div>
            </div>

            {/* Hidden Input field for Excel Upload */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".xlsx, .xls"
                onChange={handleExcelFileUploaded}
            />

            {/* Remarks Modal Component */}
            <Modal
                show={showRemarksModal}
                onHide={() => setShowRemarksModal(false)}
                centered
                className="remarks-modal"
            >
                <Modal.Header className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-24">Add Remarks</Modal.Title>
                    <div
                        className="close-btn-custom"
                        onClick={() => setShowRemarksModal(false)}
                    >
                        <CloseIc style={{ width: "16px" }} />
                    </div>
                </Modal.Header>
                <Modal.Body className="pt-4">
                    <div className="remarks-form">
                        <label className="fs-14 fw-600 mb-2">
                            Remarks<span className="text-danger">*</span>
                        </label>
                        <textarea
                            className="form-control remarks-textarea"
                            placeholder="Enter here..."
                            rows={4}
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                        />
                        <div className="d-flex justify-content-end gap-3 mt-auto">
                            <button
                                className="btn-cancel-custom"
                                onClick={() => setShowRemarksModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-submit-custom"
                                onClick={handleSaveRemarks}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Import Bulk Excel Modal */}
            <Modal
                show={showImportModal}
                onHide={() => {
                    setShowImportModal(false);
                    setSelectedFile(null);
                }}
                centered
                className="import-file-modal"
            >
                <Modal.Header className="border-0 p-4 pb-0 position-relative">
                    <div className="w-100">
                        <h3 className="modal-title fw-bold fs-24 mb-1">Import File</h3>
                        <p className="text-muted fs-14 mb-0">Choose an Excel file and import all the data from that.</p>
                    </div>
                    <div
                        className="close-btn-custom-import"
                        onClick={() => {
                            setShowImportModal(false);
                            setSelectedFile(null);
                        }}
                    >
                        <CloseIc style={{ width: "16px" }} />
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div
                        className="upload-area-custom d-flex flex-column align-items-center justify-content-center"
                        onClick={handleSelectExcelFileClick}
                        style={{ cursor: 'pointer' }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {selectedFile ? (
                            <>
                                <div className="upload-icon-circle mb-3" style={{ backgroundColor: "#EEF2FF" }}>
                                    <FileAddIc style={{ width: "24px", height: "24px", color: "#4F46E5" }} />
                                </div>
                                <h5 className="fs-16 fw-700 mb-1 text-primary-blue">File Selected</h5>
                                <p className="text-muted fs-13 mb-3 fw-500">{selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</p>
                                <button className="btn-select-file text-nowrap" style={{ minWidth: "max-content" }} onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}>
                                    Remove File
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="upload-icon-circle mb-3">
                                    <FileAddIc style={{ width: "24px", height: "24px" }} />
                                </div>
                                <h5 className="fs-16 fw-700 mb-1">Drag and drop your files</h5>
                                <p className="text-muted fs-12 mb-3">Only Excel file format (Max. Size 5MB)</p>
                                <button className="btn-select-file d-flex align-items-center justify-content-center gap-2 text-nowrap" style={{ minWidth: "max-content" }} onClick={(e) => { e.stopPropagation(); handleSelectExcelFileClick(); }}>
                                    <PlusImageIc style={{ width: "14px", height: "14px" }} /> Select File
                                </button>
                            </>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0 d-flex justify-content-between">
                    <button className="btn-template-custom" onClick={handleDownloadTemplate}>
                        <DownloadIc style={{ width: "16px", height: "16px" }} /> Template
                    </button>
                    <div className="d-flex gap-3">
                        <button
                            className="btn-cancel-custom-modal"
                            onClick={() => {
                                setShowImportModal(false);
                                setSelectedFile(null);
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-upload-custom"
                            onClick={handleUploadClick}
                            disabled={!selectedFile}
                        >
                            Upload
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* New / Edit Transaction Modal */}
            <BankTransactionModal
                show={showNewTxnModal}
                onHide={() => setShowNewTxnModal(false)}
                editingId={editingId}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                bankListOptions={bankListOptions}
                onSave={handleSaveTransaction}
                isSaving={isSaving}
            />

            {/* Transaction Details Offcanvas Drawer */}
            <Offcanvas
                show={showViewDrawer}
                onHide={() => setShowViewDrawer(false)}
                placement="end"
                className="transaction-view-drawer"
            >
                <Offcanvas.Header className="drawer-header-custom border-0 p-4 pb-0">
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex align-items-center gap-3">
                            <div className="bank-logo-circle d-flex align-items-center justify-content-center overflow-hidden bg-white">
                                {selectedRow?.bank_logo ? (
                                    <img
                                        src={selectedRow.bank_logo}
                                        alt={selectedRow?.bank_name}
                                        style={{ width: "28px", height: "28px", objectFit: "contain" }}
                                    />
                                ) : getBankLogo(selectedRow?.bank_name) ? (
                                    <img
                                        src={getBankLogo(selectedRow?.bank_name)}
                                        alt={selectedRow?.bank_name}
                                        style={{ width: "28px", height: "28px", objectFit: "contain" }}
                                    />
                                ) : (
                                    <i className="bi bi-bank fs-4 text-primary"></i>
                                )}
                            </div>
                            <div>
                                <h3 className="drawer-amount mb-1">₹{Number(selectedRow?.spent_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                                <p className="drawer-subtext mb-0">
                                    Transaction Date : <span className="text-primary-blue">{selectedRow?.transaction_date}</span>
                                </p>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <div className="action-icon-circle" onClick={() => handleEditClick(selectedRow.id)}><EditIc style={{ width: "16px" }} /></div>
                            <div className="action-icon-circle text-danger" onClick={() => handleDeleteClick(selectedRow.id)}><TrashIc style={{ width: "16px" }} /></div>
                            <div className="close-drawer-btn ms-2" onClick={() => setShowViewDrawer(false)}>
                                <CloseIc style={{ width: "16px" }} />
                            </div>
                        </div>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-4 pt-4 d-flex flex-column position-relative" style={{ height: "calc(100% - 80px)", overflowY: "auto" }}>
                    {isDrawerLoading && (
                        <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center" style={{ top: 0, left: 0, backgroundColor: "rgba(255, 255, 255, 0.7)", zIndex: 10 }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    <div className="drawer-cards-grid mb-4">
                        <div className="drawer-info-card text-center">
                            <span className="card-label">Value date</span>
                            <span className="card-value">{selectedRow?.value_date}</span>
                        </div>
                        <div className="drawer-info-card text-center">
                            <span className="card-label">Balance amount</span>
                            <span className="card-value">₹{Number(selectedRow?.balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="drawer-info-card text-center">
                            <span className="card-label">Cheque No.</span>
                            <span className="card-value">{selectedRow?.cheque_number || "---"}</span>
                        </div>
                    </div>

                    <div className="other-details-section mb-4">
                        <h5 className="section-title mb-3">Other Details</h5>
                        <div className="detail-row mb-3">
                            <span className="detail-label">Bank Name</span>
                            <div className="detail-value d-flex align-items-center gap-2">
                                {selectedRow?.bank_logo ? (
                                    <img
                                        src={selectedRow.bank_logo}
                                        alt={selectedRow?.bank_name}
                                        style={{ width: "20px", height: "20px", objectFit: "contain" }}
                                    />
                                ) : getBankLogo(selectedRow?.bank_name) ? (
                                    <img
                                        src={getBankLogo(selectedRow?.bank_name)}
                                        alt={selectedRow?.bank_name}
                                        style={{ width: "20px", height: "20px", objectFit: "contain" }}
                                    />
                                ) : null}
                                <span className="fw-600 text-dark">{selectedRow?.bank_name}</span>
                            </div>
                        </div>
                        <div className="detail-row mb-3">
                            <span className="detail-label">Created Type</span>
                            <div className="detail-value">
                                <span className={`badge-${selectedRow?.created_from === "Manual" ? "manual" : "excel"}`}>
                                    {selectedRow?.created_from === "Manual" ? "MANUAL" : "EXCEL"}
                                </span>
                            </div>
                        </div>
                        <div className="detail-row mb-3 align-items-start">
                            <span className="detail-label pt-1">Description</span>
                            <div className="detail-value text-muted-dark">
                                {selectedRow?.description || "---"}
                            </div>
                        </div>
                    </div>

                    <div className="remarks-view-section mb-4">
                        <h5 className="section-title mb-3">Remarks</h5>
                        <p className="remarks-content text-muted-dark fs-14">
                            {selectedRow?.remarks || "No remarks added yet."}
                        </p>
                    </div>

                    <div className="drawer-footer mt-auto pt-3">
                        <div className="d-flex align-items-center gap-2 text-muted fs-12">
                            <CalenderIc style={{ width: "14px" }} />
                            Created: {selectedRow?.created_at || "System"}
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Delete confirmation modal */}
            <DeleteModal
                title="Bank Transaction"
                isOpen={isOpenDelete}
                onClose={() => setIsOpenDelete(false)}
                onDelete={deleteBankTransaction}
            />

            {/* Snackbar feedback notification */}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
                <Toast className="d-inline-block m-1" onClose={() => setShowSnackbar(false)} show={showSnackbar} delay={3000} autohide bg={snackbarStatus ? "success" : "danger"}>
                    <Toast.Body className="text-white fw-500">{snackbarMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </div>
    );
};

export default BankTransactionListingPage;
